import axios, { type AxiosError } from 'axios';

// Create a function to get the auth token
const getAuthToken = (): string | null => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('authToken');
  }
  return null;
};

// Create a function to get the current tenant ID
const getCurrentTenantId = (): string | null => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('selectedTenantId');
  }
  return null;
};

// Create a function to get the current facility group ID
const getCurrentFacilityGroupId = (): string | null => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('selectedFacilityGroupId');
  }
  return null;
};

// Create axios instance with base URL
// In production, we use relative URLs since nginx proxies /api and /graphql
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for cookies, authorization headers with HTTPS
});

// Request interceptor for API calls
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    const tenantId = getCurrentTenantId();
    const facilityGroupId = getCurrentFacilityGroupId();
    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    if (tenantId && config.headers) {
      config.headers['x-tenant-id'] = tenantId;
    }
    
    if (facilityGroupId && config.headers) {
      config.headers['x-facility-group-id'] = facilityGroupId;
    }
    
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor for API calls
apiClient.interceptors.response.use(
  (response) => {
    // Handle REST API responses that indicate failure but return 200 status
    if (response.data?.success === false) {
      // Create an error object with the enhanced details preserved
      const restError = new Error(response.data.error || 'API request failed') as Error & {
        response: typeof response;
        validation?: any;
        ruleFailureDetails?: any;
        availableTransitions?: any;
        currentFacts?: any;
      };
      
      restError.response = response;
      
      // Preserve enhanced error details from REST API response
      if (response.data.validation) {
        restError.validation = response.data.validation;
      }
      if (response.data.ruleFailureDetails) {
        restError.ruleFailureDetails = response.data.ruleFailureDetails;
      }
      if (response.data.availableTransitions) {
        restError.availableTransitions = response.data.availableTransitions;
      }
      if (response.data.currentFacts) {
        restError.currentFacts = response.data.currentFacts;
      }
      
      return Promise.reject(restError);
    }
    
    // For GraphQL, check if there are errors in the response
    if (response.data?.errors && 
        response.data.errors.length > 0 && 
        response.config?.url?.includes('/graphql')) { // Actually a GraphQL endpoint
      const error = new Error('GraphQL Error') as Error & { response: typeof response };
      error.response = response;
      return Promise.reject(error);
    }
    
    return response; // Automatically return the data from the response
  },
  async (error: AxiosError) => {
    interface ErrorResponse {
      message?: string;
      errors?: Array<{ message: string }>;
      validation?: any; // Enhanced error details from workflow transitions
      ruleFailureDetails?: any;
      availableTransitions?: any;
      currentFacts?: any;
    }
    const errorData = error.response?.data as ErrorResponse | undefined;
    
    // Check for GraphQL errors
    if (errorData?.errors && errorData.errors.length > 0) {
      const errorMessage = errorData.errors.map(e => e.message).join(', ');
      const graphqlError = new Error(errorMessage) as Error & { response: typeof error.response };
      graphqlError.response = error.response;
      return Promise.reject(graphqlError);
    }
    
    // For REST API errors, preserve enhanced error details
    const errorMessage = errorData?.message || 
                        error.message || 
                        'An unexpected error occurred';
    
    // Create enhanced error object that preserves validation details
    const enhancedError = new Error(errorMessage) as Error & { 
      response: typeof error.response;
      validation?: any;
      ruleFailureDetails?: any;
      availableTransitions?: any;
      currentFacts?: any;
    };
    
    enhancedError.response = error.response;
    
    // Preserve enhanced error details if they exist
    if (errorData?.validation) {
      enhancedError.validation = errorData.validation;
    }
    if (errorData?.ruleFailureDetails) {
      enhancedError.ruleFailureDetails = errorData.ruleFailureDetails;
    }
    if (errorData?.availableTransitions) {
      enhancedError.availableTransitions = errorData.availableTransitions;
    }
    if (errorData?.currentFacts) {
      enhancedError.currentFacts = errorData.currentFacts;
    }
    
    return Promise.reject(enhancedError);
  }
);

// Helper function to handle API errors
const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    interface ErrorResponse {
      message?: string;
    }
    const errorData = error.response?.data as ErrorResponse | undefined;
    return errorData?.message || error.message || 'Network error occurred';
  }
  return error instanceof Error ? error.message : 'An unexpected error occurred';
};

// Enhanced permission-related API functions
export const permissionService = {
  // Get available permissions from sources (legacy)
  async getAvailablePermissions() {
    const response = await apiClient.get('/api/source-capabilities');
    const permissions = new Set<string>();
    
    // Extract permissions from all source capabilities
    response.data.forEach((capability: any) => {
      if (capability.capabilities?.permissions) {
        capability.capabilities.permissions.forEach((perm: any) => {
          if (perm.permissionKey) {
            permissions.add(perm.permissionKey);
          }
        });
      }
    });
    
    return Array.from(permissions).sort();
  },

  // NEW: Get permissions grouped by source system
  async getPermissionsBySource(sourceSystemId?: string) {
    const endpoint = sourceSystemId 
      ? `/api/permissions/source-systems/${sourceSystemId}`
      : '/api/permissions/source-systems';
    const response = await apiClient.get(endpoint);
    return response.data;
  },

  // NEW: Get permissions with metadata for typeahead
  async getPermissionsWithMetadata(sourceSystemId?: string, category?: string, active?: boolean) {
    const params: any = {};
    if (sourceSystemId) params.source = sourceSystemId;
    if (category) params.category = category;
    if (active !== undefined) params.active = active;
    
    const response = await apiClient.get('/api/permissions/detailed', { params });
    return response.data;
  },

  // NEW: Search permissions across sources
  async searchPermissions(query: string, sourceSystemId?: string, limit?: number) {
    const params: any = { q: query };
    if (sourceSystemId) params.source = sourceSystemId;
    if (limit) params.limit = limit;
    
    const response = await apiClient.get('/api/permissions/search', { params });
    return response.data;
  },

  // NEW: Sync permissions from source system
  async syncPermissions(sourceSystemId: string, options?: { forceSync?: boolean; permissionFilters?: string[] }) {
    const response = await apiClient.post(`/api/permissions/source-systems/${sourceSystemId}/sync`, options || {});
    return response.data;
  },

  // NEW: Get permission sync status
  async getPermissionSyncStatus(sourceSystemId: string) {
    const response = await apiClient.get(`/api/permissions/sync-status/${sourceSystemId}`);
    return response.data;
  },

  // Get permissions for a specific state
  async getStatePermissions(workflowId: string, stateId: string) {
    const response = await apiClient.get(`/api/workflows/${workflowId}/states/${stateId}/permissions`);
    return response.data;
  },

  // Update permissions for a state
  async updateStatePermissions(workflowId: string, stateId: string, permissions: any) {
    const response = await apiClient.put(`/api/workflows/${workflowId}/states/${stateId}/permissions`, {
      permissions
    });
    return response.data;
  },

  // Get permissions for a specific transition
  async getTransitionPermissions(workflowId: string, transitionId: string) {
    const response = await apiClient.get(`/api/workflows/${workflowId}/transitions/${transitionId}/permissions`);
    return response.data;
  },

  // Update permissions for a transition
  async updateTransitionPermissions(workflowId: string, transitionId: string, permissions: any) {
    const response = await apiClient.put(`/api/workflows/${workflowId}/transitions/${transitionId}/permissions`, {
      permissions
    });
    return response.data;
  },

  // Get all element permissions for a workflow
  async getWorkflowElementPermissions(workflowId: string) {
    const response = await apiClient.get(`/api/workflows/${workflowId}/element-permissions`);
    return response.data;
  }
};

// Facility group management API functions
export const facilityGroupService = {
  // Get all facility groups for the current tenant
  async getFacilityGroups(includeInactive = false) {
    const response = await apiClient.get('/api/facility-groups', {
      params: { includeInactive },
    });
    return response.data;
  },

  // Get facility group hierarchy
  async getFacilityGroupHierarchy(rootId?: string) {
    const response = await apiClient.get('/api/facility-groups/hierarchy', {
      params: { rootId },
    });
    return response.data;
  },

  // Get a specific facility group
  async getFacilityGroup(id: string) {
    const response = await apiClient.get(`/api/facility-groups/${id}`);
    return response.data;
  },

  // Create a new facility group
  async createFacilityGroup(data: {
    name: string;
    code: string;
    description?: string;
    parentId?: string;
    settings?: any;
    metadata?: any;
  }) {
    const response = await apiClient.post('/api/facility-groups', data);
    return response.data;
  },

  // Update a facility group
  async updateFacilityGroup(id: string, data: {
    name?: string;
    code?: string;
    description?: string;
    settings?: any;
    metadata?: any;
    isActive?: boolean;
  }) {
    const response = await apiClient.put(`/api/facility-groups/${id}`, data);
    return response.data;
  },

  // Delete (deactivate) a facility group
  async deleteFacilityGroup(id: string) {
    const response = await apiClient.delete(`/api/facility-groups/${id}`);
    return response.data;
  },

  // Get all facilities
  async getFacilities(facilityGroupId?: string) {
    const response = await apiClient.get('/api/facility-groups/facilities', {
      params: { facilityGroupId },
    });
    return response.data;
  },

  // Create a new facility
  async createFacility(data: {
    name: string;
    code: string;
    facilityGroupId: string;
    type: string;
    address?: any;
    contactInfo?: any;
    settings?: any;
    metadata?: any;
  }) {
    const response = await apiClient.post('/api/facility-groups/facilities', data);
    return response.data;
  },

  // Assign user to facility group
  async assignUserToFacilityGroup(data: {
    userId: string;
    facilityGroupId: string;
    role?: string;
    permissions?: string[];
    isDefault?: boolean;
    expiresAt?: Date;
  }) {
    const response = await apiClient.post('/api/facility-groups/assignments', data);
    return response.data;
  },

  // Remove user from facility group
  async removeUserFromFacilityGroup(userId: string, facilityGroupId: string) {
    const response = await apiClient.delete(
      `/api/facility-groups/assignments/${userId}/${facilityGroupId}`,
    );
    return response.data;
  },

  // Get user's facility groups
  async getUserFacilityGroups(userId: string) {
    const response = await apiClient.get(`/api/facility-groups/users/${userId}/groups`);
    return response.data;
  },

};

// Export the API client and helper functions
export { apiClient, handleApiError };
